import { memo, ReactElement, useCallback, useMemo } from 'react';

import {
  getRepeatedFieldGroupIndex,
  OnAnswerChange,
  RenderingField,
  RenderingRepeatedFieldGroup,
} from '@zinnia/form-engine-sdk';
import { Language } from '@breathelife/types';

import { StyleVariant } from '../../../FieldGenerator/FieldGenerator';
import { QuestionContent } from '../QuestionContent';
import { Container } from '../Styles';
import { RepeatableQuestionContent } from './RepeatableQuestionContent';

type RepeatableQuestionProps = {
  fieldGroup: RenderingRepeatedFieldGroup;
  onAnswerChange: OnAnswerChange;
  onAnswerComplete: (fieldId: string, answer: any, previousAnswer: any) => void;
  styleVariant: StyleVariant;
  locale?: Language;
  iconMap?: Record<string, string>;
  focusedIncompleteFieldId?: string;
};

export const RepeatableQuestion = memo(InnerRepeatableQuestion);

export function InnerRepeatableQuestion(props: RepeatableQuestionProps): ReactElement {
  const { fieldGroup, onAnswerChange, onAnswerComplete, styleVariant, locale, iconMap, focusedIncompleteFieldId } =
    props;
  const repeatedFieldGroupIndex: number = getRepeatedFieldGroupIndex(fieldGroup);

  const addRepeatedQuestion = useCallback(() => {
    onAnswerChange([
      {
        tag: 'blueprintId',
        blueprintId: fieldGroup.blueprintId,
        nodeId: fieldGroup.nodeId,
        value: {},
        effects: fieldGroup.effects,
        blueprintIdScope: {
          ...fieldGroup.scope.repeatedInstanceIdentifierContext.byBlueprintId,
          [fieldGroup.blueprintId]: repeatedFieldGroupIndex + 1,
        },
        nodeIdScope: {
          ...fieldGroup.scope.repeatedInstanceIdentifierContext.byNodeId,
          [fieldGroup.nodeId]: repeatedFieldGroupIndex + 1,
        },
      },
    ]);
  }, [
    onAnswerChange,
    fieldGroup.blueprintId,
    fieldGroup.nodeId,
    fieldGroup.effects,
    fieldGroup.scope.repeatedInstanceIdentifierContext.byBlueprintId,
    fieldGroup.scope.repeatedInstanceIdentifierContext.byNodeId,
    repeatedFieldGroupIndex,
  ]);

  const deleteRepeatedQuestion = useCallback(() => {
    onAnswerChange([
      {
        tag: 'blueprintId',
        blueprintId: fieldGroup.blueprintId,
        nodeId: fieldGroup.nodeId,
        value: undefined,
        effects: fieldGroup.effects,
        blueprintIdScope: fieldGroup.scope.repeatedInstanceIdentifierContext.byBlueprintId,
        nodeIdScope: fieldGroup.scope.repeatedInstanceIdentifierContext.byNodeId,
      },
    ]);
  }, [
    onAnswerChange,
    fieldGroup.blueprintId,
    fieldGroup.effects,
    fieldGroup.scope.repeatedInstanceIdentifierContext.byBlueprintId,
    fieldGroup.scope.repeatedInstanceIdentifierContext.byNodeId,
    fieldGroup.nodeId,
  ]);

  const renderContent = useMemo(() => {
    return renderRepeatableQuestionContent(onAnswerChange, styleVariant, locale, iconMap, focusedIncompleteFieldId);
  }, [iconMap, locale, onAnswerChange, styleVariant]);

  return (
    <Container
      styleVariant={styleVariant}
      key={fieldGroup.surrogateId ?? fieldGroup.id}
      data-testid={'repeatable_question'}
    >
      <RepeatableQuestionContent
        fieldGroup={fieldGroup}
        onQuestionAdd={addRepeatedQuestion}
        onQuestionDelete={deleteRepeatedQuestion}
        onAnswerComplete={onAnswerComplete}
        renderContent={renderContent}
        styleVariant={styleVariant}
      />
    </Container>
  );
}

function renderRepeatableQuestionContent(
  onAnswerChange: OnAnswerChange,
  styleVariant: StyleVariant,
  locale?: Language,
  iconMap?: Record<string, string>,
  focusedIncompleteFieldId?: string,
) {
  return (
    fields: RenderingField[],
    onAnswerComplete: (fieldId: string, answer: any, previousAnswer: any, repeatedQuestionIndex?: number) => void,
  ) => (
    <QuestionContent
      fields={fields}
      onAnswerChange={onAnswerChange}
      onAnswerComplete={onAnswerComplete}
      styleVariant={styleVariant}
      locale={locale}
      iconMap={iconMap}
      focusedIncompleteFieldId={focusedIncompleteFieldId}
    />
  );
}
